import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import api from "../../lib/api";
import { uploadImage } from "./EditableImage";
import { EditBadge } from "./Editable";
import { useContent } from "../../context/ContentContext";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const SPECS = {
  product: (ctx) => [
    { key: "name", label: "Name", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "image", label: "Photo", type: "image" },
    { key: "category_id", label: "Category", type: "select", options: (ctx.categories || []).map((c) => [c.id, c.name]) },
    { key: "dietary_tags", label: "Dietary tags", type: "tags" },
    { key: "in_stock", label: "Available to order", type: "switch", offLabel: "Unavailable (shows Sold out)" },
    { key: "active", label: "Shown on the menu", type: "switch", offLabel: "Hidden (find it under Hidden items)" },
    { key: "base_price", label: "Price", type: "readonly", note: "Prices are changed in the POS — the website follows the POS." },
  ],
  category: () => [
    { key: "name", label: "Name", type: "text" },
    { key: "sort", label: "Order (lower shows first)", type: "number" },
    { key: "image", label: "Image (optional)", type: "image" },
    { key: "hidden", label: "Hidden", type: "switch", invert: true, onLabel: "Shown", offLabel: "Hidden — its items are hidden too" },
  ],
  business: () => [
    { key: "name", label: "Name", type: "text" },
    { key: "tagline", label: "Tagline", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "image", label: "Hero image", type: "image" },
    { key: "seo.title", label: "SEO title", type: "text" },
    { key: "seo.description", label: "SEO description", type: "textarea" },
    { key: "active", label: "Shown on the site", type: "switch", offLabel: "Hidden" },
  ],
  location: () => [
    { key: "address", label: "Address", type: "text" },
    { key: "phone", label: "Phone", type: "text" },
    { key: "hours", label: "Hours (24h, HH:MM — blank = closed)", type: "hours" },
  ],
};

const getPath = (o, p) => p.split(".").reduce((a, k) => (a == null ? a : a[k]), o);

export function EntityEditor({ type, entity, open, onOpenChange, onSaved, ctx = {} }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  useEffect(() => { if (open && type === "product") api.get("/cms/tags").then(({ data }) => setTagOptions(data)).catch(() => {}); }, [open, type]);
  useEffect(() => {
    if (!open || !entity) return;
    const f = {};
    SPECS[type](ctx).forEach((s) => { f[s.key] = getPath(entity, s.key); });
    if (type === "location") f.hours = { ...(entity.hours || {}) };
    setForm(f);
  }, [open, entity, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const fields = {};
      SPECS[type](ctx).forEach((s) => {
        if (s.type === "readonly") return;
        const v = form[s.key];
        if (s.key.includes(".")) { const [a, b] = s.key.split("."); fields[a] = { ...(fields[a] || {}), [b]: v }; }
        else if (v !== getPath(entity, s.key)) fields[s.key] = s.type === "number" ? Number(v) : v;
      });
      if (fields.seo) { const old = entity.seo || {}; if (Object.keys(fields.seo).every((k) => fields.seo[k] === old[k])) delete fields.seo; }
      if (!Object.keys(fields).length) { onOpenChange(false); return; }
      const { data } = await api.patch(`/cms/${type}/${entity.id}`, { fields });
      toast.success("Saved — live now");
      onSaved?.(data.entity);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!entity) return null;
  const inputCls = "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto z-[90]" data-testid={`entity-editor-${type}`}>
        <DialogHeader><DialogTitle className="font-display">Edit {type}: {entity.name || entity.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {SPECS[type](ctx).map((s) => (
            <div key={s.key} data-testid={`field-${s.key.replace(".", "-")}`}>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">{s.label}</label>
              {s.type === "text" && <input className={inputCls} value={form[s.key] ?? ""} onChange={(e) => set(s.key, e.target.value)} />}
              {s.type === "number" && <input type="number" className={inputCls} value={form[s.key] ?? 0} onChange={(e) => set(s.key, e.target.value)} />}
              {s.type === "textarea" && <textarea rows={4} className={inputCls} value={form[s.key] ?? ""} onChange={(e) => set(s.key, e.target.value)} />}
              {s.type === "readonly" && (
                <p className="text-sm text-neutral-600 bg-neutral-50 border border-dashed rounded-lg px-3 py-2" data-testid="price-readonly">
                  {form[s.key] ? `$${Number(form[s.key]).toFixed(2)}` : "Price TBD"} <span className="block text-xs text-neutral-500 mt-0.5">{s.note}</span>
                </p>
              )}
              {s.type === "select" && (
                <select className={inputCls} value={form[s.key] ?? ""} onChange={(e) => set(s.key, e.target.value || null)}>
                  <option value="">— none —</option>
                  {s.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {s.type === "switch" && (
                <div className="flex items-center gap-3">
                  <Switch checked={s.invert ? !form[s.key] : !!form[s.key]} onCheckedChange={(v) => set(s.key, s.invert ? !v : v)} data-testid={`switch-${s.key}`} />
                  <span className="text-sm">{(s.invert ? !form[s.key] : form[s.key]) ? (s.onLabel || "Yes") : s.offLabel}</span>
                </div>
              )}
              {s.type === "tags" && (
                <div className="flex flex-wrap gap-1.5">
                  {tagOptions.map((t) => {
                    const on = (form.dietary_tags || []).includes(t);
                    return (
                      <button type="button" key={t} data-testid={`tag-${t.replace(/\s+/g, "-")}`}
                        onClick={() => set("dietary_tags", on ? form.dietary_tags.filter((x) => x !== t) : [...(form.dietary_tags || []), t])}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? "bg-green-700 text-white border-green-700" : "bg-white border-neutral-300"}`}>{t}</button>
                    );
                  })}
                </div>
              )}
              {s.type === "image" && (
                <div className="flex items-center gap-3">
                  {form[s.key] && <img src={form[s.key]} alt="" className="w-16 h-16 rounded-lg object-cover border" />}
                  <label className="text-sm font-semibold underline cursor-pointer">
                    Upload new photo
                    <input type="file" accept="image/*" className="hidden" data-testid={`upload-${s.key}`}
                      onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { set(s.key, await uploadImage(f)); } catch { toast.error("Upload failed"); } }} />
                  </label>
                </div>
              )}
              {s.type === "hours" && (
                <div className="space-y-1.5">
                  {DAYS.map((d) => {
                    const iv = (form.hours || {})[d]?.[0] || ["", ""];
                    const upd = (i, v) => { const cur = [...iv]; cur[i] = v; set("hours", { ...form.hours, [d]: cur[0] && cur[1] ? [cur] : [] }); };
                    return (
                      <div key={d} className="flex items-center gap-2 text-sm">
                        <span className="w-9 uppercase text-xs font-bold text-neutral-500">{d}</span>
                        <input type="time" value={iv[0] || ""} onChange={(e) => upd(0, e.target.value)} className="rounded border px-2 py-1" />
                        <span>–</span>
                        <input type="time" value={iv[1] || ""} onChange={(e) => upd(1, e.target.value)} className="rounded border px-2 py-1" />
                        {!iv[0] && <span className="text-xs text-neutral-400">closed</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-neutral-500">Every save keeps the previous version. Restore from the edit bar → History.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full border text-sm font-semibold">Cancel</button>
            <button type="button" data-testid="entity-save" onClick={save} disabled={busy} className="px-4 py-2 rounded-full bg-black text-white text-sm font-bold disabled:opacity-50">Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Drop-in pencil that opens the editor for one entity. Renders nothing for customers.
export function EditEntityButton({ type, entity, onSaved, ctx, className = "", label = "Edit" }) {
  const { editMode } = useContent();
  const [open, setOpen] = useState(false);
  if (!editMode || !entity) return null;
  return (
    <>
      <span className={`z-30 ${className}`}><EditBadge label={label} testId={`edit-${type}-${entity.id}`} onClick={() => setOpen(true)} /></span>
      <EntityEditor type={type} entity={entity} open={open} onOpenChange={setOpen} onSaved={onSaved} ctx={ctx} />
    </>
  );
}
